import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

// Static animation configs - defined outside component to prevent recreation
const flourishPositions = {
  topLeft: 'top-0 left-0',
  topRight: 'top-0 right-0',
  bottomRight: 'bottom-0 right-0',
  bottomLeft: 'bottom-0 left-0'
};

const flourishRotations = {
  topLeft: 0,
  topRight: 90,
  bottomRight: 180,
  bottomLeft: 270
};

const patternTransition1 = { duration: 60, repeat: Infinity, repeatType: 'reverse', ease: 'linear' };
const patternTransition2 = { duration: 45, repeat: Infinity, repeatType: 'reverse', ease: 'linear' };
const patternTransition3 = { duration: 30, repeat: Infinity, repeatType: 'reverse', ease: 'linear' };
const shimmerTransition = { duration: 2, repeat: Infinity, repeatDelay: 3 };
const pulseGlowTransition = { duration: 3, repeat: Infinity, ease: "easeInOut" };
const heartPulseTransition = { scale: { duration: 2, repeat: Infinity, delay: 1.5 }, rotate: { duration: 0.8, delay: 1.2 } };
const nameShimmerTransition = { duration: 3, repeat: Infinity, repeatDelay: 2 };

const glowAnimation = {
  boxShadow: [
    '0 0 20px rgba(212, 175, 55, 0.3), 0 0 40px rgba(212, 175, 55, 0.1)',
    '0 0 30px rgba(212, 175, 55, 0.5), 0 0 60px rgba(212, 175, 55, 0.2)',
    '0 0 20px rgba(212, 175, 55, 0.3), 0 0 40px rgba(212, 175, 55, 0.1)',
  ]
};

// Ornate corner flourish SVG component
const CornerFlourish = ({ position, delay = 0 }) => {
  const pathTransition = useMemo(() => ({ duration: 1.5, delay: delay + 0.3, ease: "easeInOut" }), [delay]);
  const innerPathTransition = useMemo(() => ({ duration: 1.2, delay: delay + 0.5, ease: "easeInOut" }), [delay]);
  const circleTransition = useMemo(() => ({ duration: 0.3, delay: delay + 1 }), [delay]);
  const containerTransition = useMemo(() => ({ duration: 0.8, delay: delay, ease: "easeOut" }), [delay]);

  return (
    <motion.div 
      className={`absolute ${flourishPositions[position]} w-16 h-16 sm:w-20 sm:h-20`}
      style={{ transform: `rotate(${flourishRotations[position]}deg)` }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={containerTransition}
    >
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <motion.path
          d="M5,5 Q5,50 50,50 Q20,50 20,80 M50,50 Q50,20 80,20"
          fill="none"
          stroke="#D4AF37"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={pathTransition}
        />
        <motion.path
          d="M10,10 Q10,40 40,40 M40,40 Q40,10 70,10"
          fill="none"
          stroke="#D4AF37"
          strokeWidth="1.5"
          opacity="0.6"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={innerPathTransition}
        />
        <motion.circle
          cx="50"
          cy="50"
          r="3"
          fill="#D4AF37"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={circleTransition}
        />
      </svg>
    </motion.div>
  );
};

// Animated swirl/flourish
const AnimatedSwirl = ({ className, delay = 0 }) => {
  const containerTransition = useMemo(() => ({ duration: 0.5, delay }), [delay]);
  const mainPathTransition = useMemo(() => ({ duration: 2, delay: delay + 0.2, ease: "easeInOut" }), [delay]);
  const secondaryPathTransition = useMemo(() => ({ duration: 1.5, delay: delay + 0.5, ease: "easeInOut" }), [delay]);
  const tertiaryPathTransition = useMemo(() => ({ duration: 1.5, delay: delay + 0.7, ease: "easeInOut" }), [delay]);

  return (
    <motion.svg 
      viewBox="0 0 200 30" 
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={containerTransition}
    >
      <motion.path
        d="M0,15 Q25,5 50,15 T100,15 T150,15 T200,15"
        fill="none"
        stroke="#D4AF37"
        strokeWidth="1.5"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={mainPathTransition}
      />
      <motion.path
        d="M20,15 Q35,25 50,15 Q65,5 80,15"
        fill="none"
        stroke="#D4AF37"
        strokeWidth="1"
        opacity="0.5"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={secondaryPathTransition}
      />
      <motion.path
        d="M120,15 Q135,25 150,15 Q165,5 180,15"
        fill="none"
        stroke="#D4AF37"
        strokeWidth="1"
        opacity="0.5"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={tertiaryPathTransition}
      />
    </motion.svg>
  );
};

const InvitationCard = () => {
  const { setLanguage, hasSelectedLanguage } = useLanguage();
  const [isOpening, setIsOpening] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [showButtons, setShowButtons] = useState(false);

  // Memoized animation configs
  const cardAnimation = useMemo(() => (
    isOpening 
      ? { rotateY: -30, scale: 0.9, x: -50, opacity: 0 }
      : { rotateY: 0, scale: 1, x: 0, opacity: 1 }
  ), [isOpening]);

  const cardTransition = useMemo(() => ({
    duration: isOpening ? 0.8 : 1,
    ease: [0.4, 0, 0.2, 1]
  }), [isOpening]);

  const patternAnimation1 = useMemo(() => ({ backgroundPosition: ['0% 0%', '100% 100%'] }), []);
  const patternAnimation2 = useMemo(() => ({ backgroundPosition: ['100% 0%', '0% 100%'] }), []);
  const patternAnimation3 = useMemo(() => ({ backgroundPosition: ['50% 0%', '50% 100%'] }), []);

  const shimmerAnimation = useMemo(() => ({ x: ['-100%', '200%'] }), []);
  const heartAnimation = useMemo(() => ({ scale: [1, 1.1, 1], rotate: 0 }), []);
  
  // Pre-computed sparkle configs
  const sparkleConfigs = useMemo(() => 
    [...Array(24)].map((_, i) => ({
      id: `sparkle-${i}`,
      size: Math.random() * 10 + 4,
      background: i % 2 === 0 ? '#D4AF37' : '#800020',
      boxShadow: `0 0 10px ${i % 2 === 0 ? '#D4AF37' : '#800020'}`,
      targetX: (Math.random() - 0.5) * 400,
      targetY: (Math.random() - 0.5) * 500,
      delay: i * 0.03
    })), []);

  useEffect(() => {
    const timer1 = setTimeout(() => setShowContent(true), 800);
    const timer2 = setTimeout(() => setShowButtons(true), 3000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const handleLanguageSelect = (lang) => {
    setIsOpening(true);
    setTimeout(() => {
      setLanguage(lang);
    }, 1000);
  };

  if (hasSelectedLanguage) return null;

  return (
    <AnimatePresence mode="wait">
      {!hasSelectedLanguage && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="invitation-overlay"
        >
          {/* Animated Background with moving mehndi/paisley pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#FFF8F0] via-[#F5E6D3] to-[#FFF8F0]">
            {/* Moving pattern layer 1 */}
            <motion.div 
              className="absolute inset-0 opacity-[0.07]"
              animate={patternAnimation1}
              transition={patternTransition1}
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23800020'%3E%3Cpath d='M40 10c-16.57 0-30 13.43-30 30s13.43 30 30 30 30-13.43 30-30-13.43-30-30-30zm0 50c-11.05 0-20-8.95-20-20s8.95-20 20-20 20 8.95 20 20-8.95 20-20 20z'/%3E%3Ccircle cx='40' cy='40' r='8'/%3E%3C/g%3E%3C/svg%3E")`,
                backgroundSize: '80px 80px'
              }}
            />
            {/* Moving pattern layer 2 - opposite direction */}
            <motion.div 
              className="absolute inset-0 opacity-[0.05]"
              animate={patternAnimation2}
              transition={patternTransition2}
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5C15 5 5 15 5 30s10 25 25 25 25-10 25-25S45 5 30 5zm0 40c-8.28 0-15-6.72-15-15s6.72-15 15-15 15 6.72 15 15-6.72 15-15 15z' fill='%23D4AF37' fill-opacity='1'/%3E%3Ccircle cx='30' cy='30' r='5' fill='%23D4AF37'/%3E%3C/svg%3E")`,
                backgroundSize: '60px 60px'
              }}
            />
            {/* Floating paisley elements */}
            <motion.div 
              className="absolute inset-0 opacity-[0.04]"
              animate={patternAnimation3}
              transition={patternTransition3}
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 20c-20 0-30 15-30 30s15 30 30 25c15-5 25-15 25-25s-5-30-25-30z' fill='none' stroke='%23800020' stroke-width='2'/%3E%3Ccircle cx='45' cy='45' r='8' fill='%23800020'/%3E%3C/svg%3E")`,
                backgroundSize: '100px 100px'
              }}
            />
          </div>

          {/* Card Container with 3D perspective */}
          <div className="relative w-full max-w-md" style={{ perspective: '1200px' }}>
            
            {/* Main Card with pulsing gold border glow */}
            <motion.div
              className="relative rounded-lg overflow-visible"
              initial={{ rotateY: 0, scale: 0.9, opacity: 0 }}
              animate={cardAnimation}
              transition={cardTransition}
              style={{
                transformStyle: 'preserve-3d',
                transformOrigin: 'left center'
              }}
            >
              {/* Pulsing gold glow effect */}
              <motion.div
                className="absolute -inset-1 rounded-lg"
                animate={glowAnimation}
                transition={pulseGlowTransition}
              />

              {/* Card background */}
              <div className="relative bg-[#FFFDF8] rounded-lg overflow-hidden shadow-2xl">
                
                {/* Ornate corner decorations */}
                <CornerFlourish position="topLeft" delay={0.5} />
                <CornerFlourish position="topRight" delay={0.7} />
                <CornerFlourish position="bottomLeft" delay={0.9} />
                <CornerFlourish position="bottomRight" delay={1.1} />

                {/* Top gold border with shimmer */}
                <motion.div 
                  className="h-2 bg-gradient-to-r from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37] relative overflow-hidden"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
                    animate={shimmerAnimation}
                    transition={shimmerTransition}
                    style={{ opacity: 0.4 }}
                  />
                </motion.div>
                
                {/* Side borders */}
                <motion.div 
                  className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37]"
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                />
                <motion.div 
                  className="absolute right-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37]"
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                />
                
                {/* Decorative header with animated flourish */}
                <div className="flex items-center justify-center py-5 px-4">
                  <AnimatedSwirl className="w-20 h-6" delay={1} />
                  <motion.svg 
                    className="w-8 h-8 mx-4 text-[#D4AF37]" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={heartAnimation}
                    transition={heartPulseTransition}
                  >
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </motion.svg>
                  <AnimatedSwirl className="w-20 h-6 transform scale-x-[-1]" delay={1.2} />
                </div>

                {/* Content */}
                <div className="px-6 sm:px-8 pb-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 20 }}
                    transition={{ duration: 0.8 }}
                  >
                    {/* English */}
                    <motion.p 
                      className="text-center text-[#2D2424]/70 text-sm sm:text-base tracking-wide mb-2 font-light"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: showContent ? 1 : 0 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      You are cordially invited to the wedding of
                    </motion.p>
                    
                    {/* Names with shimmer effect */}
                    <motion.div
                      className="relative"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 10 }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                    >
                      <h1 className="text-center font-heading text-3xl sm:text-4xl text-[#800020] font-semibold mb-4 relative">
                        Anica & Tamjid
                        {/* Shimmer overlay */}
                        <motion.span
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent"
                          animate={shimmerAnimation}
                          transition={nameShimmerTransition}
                          style={{ mixBlendMode: 'overlay' }}
                        />
                      </h1>
                    </motion.div>

                    {/* Animated divider */}
                    <div className="flex items-center justify-center my-4">
                      <motion.div 
                        className="w-12 sm:w-16 h-px bg-[#D4AF37]"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: showContent ? 1 : 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                      />
                      <motion.div 
                        className="w-2 h-2 mx-2 bg-[#D4AF37] rounded-full"
                        initial={{ scale: 0 }}
                        animate={{ scale: showContent ? 1 : 0 }}
                        transition={{ duration: 0.3, delay: 0.8 }}
                      />
                      <motion.div 
                        className="w-12 sm:w-16 h-px bg-[#D4AF37]"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: showContent ? 1 : 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                      />
                    </div>

                    {/* Bengali */}
                    <motion.p 
                      className="text-center font-bangla text-[#2D2424]/70 text-sm sm:text-base leading-relaxed mb-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: showContent ? 1 : 0 }}
                      transition={{ duration: 0.6, delay: 0.9 }}
                    >
                      আপনাকে আন্তরিকভাবে আমন্ত্রণ জানানো হচ্ছে
                    </motion.p>
                    <motion.h2 
                      className="text-center font-bangla text-2xl sm:text-3xl text-[#800020] font-semibold leading-snug mb-6"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 10 }}
                      transition={{ duration: 0.8, delay: 1.1 }}
                    >
                      আনিকা ও তামজিদের বিবাহে
                    </motion.h2>

                    {/* Date */}
                    <motion.div 
                      className="text-center mb-6"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: showContent ? 1 : 0 }}
                      transition={{ duration: 0.6, delay: 1.3 }}
                    >
                      <p className="text-[#D4AF37] font-heading text-lg tracking-wider">
                        August 16, 2026
                      </p>
                      <p className="font-bangla text-[#D4AF37] text-base mt-1">
                        ১৬ আগস্ট ২০২৬
                      </p>
                    </motion.div>
                  </motion.div>

                  {/* Buttons */}
                  <motion.div
                    className="flex flex-col sm:flex-row gap-3 justify-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: showButtons ? 1 : 0, y: showButtons ? 0 : 20 }}
                    transition={{ duration: 0.5 }}
                  >
                    <motion.button
                      onClick={() => handleLanguageSelect('en')}
                      disabled={isOpening}
                      className="px-8 py-4 min-h-[52px] bg-[#800020] text-white font-medium rounded-lg 
                                 hover:bg-[#600018] active:scale-[0.98] transition-all duration-200
                                 shadow-lg hover:shadow-xl text-base tracking-wide
                                 disabled:opacity-50 relative overflow-hidden"
                      data-testid="select-english-btn"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="relative z-10">Enter in English</span>
                      <motion.span
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        initial={{ x: '-100%' }}
                        whileHover={{ x: '200%' }}
                        transition={{ duration: 0.6 }}
                      />
                    </motion.button>
                    <motion.button
                      onClick={() => handleLanguageSelect('bn')}
                      disabled={isOpening}
                      className="px-8 py-4 min-h-[52px] bg-[#D4AF37] text-[#2D2424] font-medium rounded-lg 
                                 font-bangla hover:bg-[#C4A030] active:scale-[0.98] transition-all duration-200
                                 shadow-lg hover:shadow-xl text-base
                                 disabled:opacity-50 relative overflow-hidden"
                      data-testid="select-bangla-btn"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="relative z-10">বাংলায় প্রবেশ করুন</span>
                      <motion.span
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                        initial={{ x: '-100%' }}
                        whileHover={{ x: '200%' }}
                        transition={{ duration: 0.6 }}
                      />
                    </motion.button>
                  </motion.div>
                </div>

                {/* Bottom decorative flourish */}
                <div className="flex items-center justify-center pb-5 px-4">
                  <AnimatedSwirl className="w-16 h-5" delay={1.8} />
                  <motion.svg 
                    className="w-5 h-5 mx-3 text-[#D4AF37] rotate-180" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 2 }}
                  >
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </motion.svg>
                  <AnimatedSwirl className="w-16 h-5 transform scale-x-[-1]" delay={2} />
                </div>

                {/* Bottom gold border with shimmer */}
                <motion.div 
                  className="h-2 bg-gradient-to-r from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37] relative overflow-hidden"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
                    animate={shimmerAnimation}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 3, delay: 1 }}
                    style={{ opacity: 0.4 }}
                  />
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Sparkle effects when opening */}
          <AnimatePresence>
            {isOpening && (
              <motion.div 
                className="absolute inset-0 pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {sparkleConfigs.map((sparkle) => (
                  <motion.div
                    key={sparkle.id}
                    className="absolute rounded-full"
                    style={{
                      width: sparkle.size,
                      height: sparkle.size,
                      left: '50%',
                      top: '50%',
                      background: sparkle.background,
                      boxShadow: sparkle.boxShadow
                    }}
                    initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                    animate={{
                      x: sparkle.targetX,
                      y: sparkle.targetY,
                      opacity: [0, 1, 0],
                      scale: [0, 1.5, 0]
                    }}
                    transition={{
                      duration: 1,
                      delay: sparkle.delay,
                      ease: "easeOut"
                    }}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InvitationCard;
